import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select, { StylesConfig } from "react-select";
import { Country, State } from "country-state-city";
import { useTheme } from "../../contexts/ThemeContext";
import { useCreateOrganisationMutation } from "../../services";
import { FormState } from "../../types";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import { adminRoutes } from "../../routers/AdminRouters";

type Option = { value: string; label: string; countryCode?: string };

const OrganisationCreate: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [createOrganisation, { isLoading, isError, error }] =
    useCreateOrganisationMutation();

  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    stateCode: "",
    country: "",
    countryCode: "",
    postal: "",
  });

  // inline errors + email regex
  const [errors, setErrors] = useState<{ email?: string; phone?: string }>({});
  const emailRegex = /^\S+@\S+\.\S+$/;

  // THEME CLASSES (match AdminCreate look/feel)
  const pageBg = theme === "dark" ? "bg-[#0B0B0B]" : "bg-gray-50";
  const titleText = theme === "dark" ? "text-gray-100" : "text-gray-900";
  const crumbText = theme === "dark" ? "text-gray-400" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";
  const cardClass =
    theme === "dark"
      ? "bg-[#1E1E1E] border border-[#2C2C2C] text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";
  const labelClass = theme === "dark" ? "text-[#F3F3F6]" : "text-gray-700";
  const dividerClass = theme === "dark" ? "bg-[#2C2C2C]" : "bg-gray-200";
  const inputBase =
    "w-full rounded-md px-3 py-2 outline-none transition-colors";
  const inputClass =
    theme === "dark"
      ? `${inputBase} bg-[#111111] border border-[#2C2C2C] placeholder-gray-500 focus:border-[#B6CB24] text-gray-100`
      : `${inputBase} bg-white border border-gray-300 placeholder-gray-500 focus:border-[#B6CB24] text-gray-900`;

  // Options
  const countryOptions: Option[] = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    []
  );

  const allStates: Option[] = useMemo(
    () =>
      State.getAllStates().map((s) => ({
        value: `${s.countryCode}:${s.isoCode}`,
        label: s.name,
        countryCode: s.countryCode,
      })),
    []
  );

  const statesForSelectedCountry = useMemo(() => {
    if (!form.countryCode) return allStates;
    return allStates.filter((s) => s.countryCode === form.countryCode);
  }, [form.countryCode, allStates]);

  // react-select theme-aware styles
  const selectStyles: StylesConfig<Option, false> = useMemo(() => {
    const isDark = theme === "dark";
    const bg = isDark ? "#111111" : "#ffffff";
    const menuBg = isDark ? "#111111" : "#ffffff";
    const border = isDark ? "#2C2C2C" : "#d1d5db";
    const hoverBorder = "#B6CB24";
    const focusedBg = isDark ? "#1E1E1E" : "#f3f4f6";
    const text = isDark ? "#e5e7eb" : "#111827";
    const subText = isDark ? "#9ca3af" : "#6b7280";

    return {
      control: (base, state) => ({
        ...base,
        backgroundColor: bg,
        borderColor: state.isFocused ? hoverBorder : border,
        boxShadow: "none",
        minHeight: 40,
        ":hover": { borderColor: state.isFocused ? hoverBorder : border },
      }),
      menu: (base) => ({ ...base, backgroundColor: menuBg, zIndex: 50 }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? focusedBg : menuBg,
        color: text,
        cursor: "pointer",
      }),
      singleValue: (base) => ({ ...base, color: text }),
      placeholder: (base) => ({ ...base, color: subText }),
      input: (base) => ({ ...base, color: text }),
      valueContainer: (base) => ({ ...base, paddingInline: 8 }),
      dropdownIndicator: (base) => ({ ...base, color: subText }),
      clearIndicator: (base) => ({ ...base, color: subText }),
      indicatorSeparator: (base) => ({ ...base, backgroundColor: border }),
    };
  }, [theme]);

  const handleTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // phone: allow digits only
    if (name === "phone") {
      const cleaned = value.replace(/\D/g, "");
      setForm((f) => ({ ...f, phone: cleaned }));
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));

    // clear email error while typing
    if (name === "email" && errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handleEmailBlur = () => {
    const email = form.email.trim();
    setErrors((prev) => ({
      ...prev,
      email: email && !emailRegex.test(email) ? "Email is invalid" : undefined,
    }));
  };

  const onCountryChange = (opt: Option | null) => {
    if (!opt) {
      setForm((f) => ({
        ...f,
        country: "",
        countryCode: "",
        state: "",
        stateCode: "",
      }));
      return;
    }
    setForm((f) => ({
      ...f,
      country: opt.label,
      countryCode: opt.value,
      state: f.countryCode === opt.value ? f.state : "",
      stateCode: f.countryCode === opt.value ? f.stateCode : "",
    }));
  };

  const onStateChange = (opt: Option | null) => {
    if (!opt) {
      setForm((f) => ({ ...f, state: "", stateCode: "" }));
      return;
    }
    const [countryCode, stateCode] = String(opt.value).split(":");
    const country = Country.getAllCountries().find(
      (c) => c.isoCode === countryCode
    );
    setForm((f) => ({
      ...f,
      state: opt.label,
      stateCode,
      country: country?.name || f.country,
      countryCode: country?.isoCode || f.countryCode,
    }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createOrganisation({
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        city: form.city,
        state: form.state,
        country: form.country,
        postal: form.postal,
      }).unwrap();
      // Success - navigate back to organisation list
      navigate("/organisation");
    } catch (err) {
      console.error("Failed to create organisation:", err);
    }
  };

  const selectedCountry = form.countryCode
    ? { value: form.countryCode, label: form.country }
    : null;

  const selectedState =
    form.stateCode && form.countryCode
      ? { value: `${form.countryCode}:${form.stateCode}`, label: form.state }
      : form.state
      ? { value: form.state, label: form.state }
      : null;
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;
  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl font-semibold ${titleText}`}>
            Create New Organisation
          </h1>
          <div className="text-sm mt-1">
            <Link to={`${adminRoutes.organisationList}`} className={crumbText}>
              All Organisations
            </Link>
            <span className={crumbSep}> &nbsp;&gt;&nbsp; </span>
            <span className="text-[#B6CB24] font-medium">
              Create New Organisation
            </span>
          </div>
        </div>
      </div>

      {/* Card */}
      <form
        onSubmit={onSubmit}
        className={`${cardClass} rounded-lg p-4 md:p-6`}
      >
        <h2 className="text-lg font-semibold mb-4 border-b pb-2 border-gray-700">
          Create New Organisation
        </h2>

        {isError && (
          <div className="mb-4 p-3 rounded-md bg-red-500/10 border border-red-500/50 text-red-500">
            Failed to create organisation. Please try again.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Organisation Name
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>Email</label>
            <input
              name="email"
              value={form.email}
              onChange={handleTextChange}
              onBlur={handleEmailBlur}
              placeholder="Enter here"
              className={`${inputClass} ${
                errors.email ? "border border-red-500" : ""
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Phone Number
            </label>
            <input
              name="phone"
              value={form.phone}
              onChange={handleTextChange}
              placeholder="Enter here"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={15}
              className={`${inputClass} ${
                errors.phone ? "border border-red-500" : ""
              }`}
            />
            {errors.phone && (
              <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label className={`block text-sm mb-1 ${labelClass}`}>Address</label>
          <input
            name="address"
            value={form.address}
            onChange={handleTextChange}
            placeholder="Enter here"
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>City</label>
            <input
              name="city"
              value={form.city}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>

          {/* State */}
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>State</label>
            <Select<Option, false>
              instanceId="state-select"
              styles={selectStyles}
              isClearable
              placeholder="Search & select"
              options={statesForSelectedCountry}
              value={selectedState}
              onChange={onStateChange}
              classNamePrefix="rs"
            />
          </div>

          {/* Country */}
          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Country
            </label>
            <Select<Option, false>
              instanceId="country-select"
              styles={selectStyles}
              isClearable
              placeholder="Search & select"
              options={countryOptions}
              value={selectedCountry}
              onChange={onCountryChange}
              classNamePrefix="rs"
            />
          </div>

          <div>
            <label className={`block text-sm mb-1 ${labelClass}`}>
              Postal / Zip code
            </label>
            <input
              name="postal"
              value={form.postal}
              onChange={handleTextChange}
              placeholder="Enter here"
              className={inputClass}
            />
          </div>
        </div>

        <div className={`h-px ${dividerClass} mb-4`} />

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={isLoading}
            className="px-8 py-1.5 rounded-[10px] border border-[#C6D92C] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: 'radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)'
            }}
          >
            {isLoading ? "Creating..." : "Create"}
          </button>
          <Link
              to={`${adminRoutes.organisationList}`}
            className={`px-8 py-1.5 rounded-md font-medium ${
              theme === "dark"
                ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            }`}
          >
            Not Now
          </Link>
        </div>
      </form>
    </div>
  );
};

export default OrganisationCreate;
